import { api } from '@/lib/api/client';
import { authFetch } from '@/api';
import type { SafetyAlertLike } from '@/components/SafetyAlertBanner';
import type {
  InventoryItem, InventoryMovement, DispensablePrescription, DispensablePrescriptionDetail, CreateStockAdjustmentInput,
  CreatePharmacyBillInput, PharmacyBillResult, Vendor, NewVendorInput,
  PurchaseOrder, PurchaseOrderDetail, NewPurchaseOrderInput, POStatus, GoodsReceipt, NewGoodsReceiptInput,
  ScheduleRegisterEntry,
  CounterCustomer, CounterCustomerDetail, RepeatMedicine,
  OutpatientPrescription, OutpatientPrescriptionInput,
  CreateOutpatientSaleInput, OutpatientSaleResult, OutpatientSale, OutpatientSaleDetail,
  PharmacyReturn, CreateReturnInput,
  LoyaltySettings, LoyaltyQuote, LoyaltyBalance, LoyaltyLedgerEntry,
  PaymentMode,
  MedicineSearchResult, NewMedicineInput,
  VendorInvoiceUpload, VendorInvoiceMobileSession, VendorInvoiceMobileSessionStatus,
  SubstitutionRequestResult, SubstitutionReview,
  DispensingSettings, DispensingSettingsAdmin, NearExpiryReport,
  InventoryUploadResult,
  InventoryFilter, PharmacyStore, PharmacyStoresSnapshot, PharmacyStoresAdminSnapshot, PharmacyChannel, PharmacyStoreType,
} from './types';

// ── Inventory upload workbook (routes/pharmacy-inventory-import.js) ──
// Raw authFetch, not the JSON client: one is a binary download, the other a
// multipart upload. authFetch adds the token and the active-branch header.

/** Downloads the pre-filled template for the active branch. */
export async function downloadInventoryTemplate(): Promise<void> {
  const res = await authFetch('/api/pharmacy-inventory-import/template');
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    throw new Error(data?.error || `Download failed (HTTP ${res.status})`);
  }
  const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')?.[1] || 'pharmacy_inventory.xlsx';
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** `dryRun` checks the file and reports what would change, saving nothing. */
export async function uploadInventoryWorkbook(file: File, dryRun: boolean): Promise<InventoryUploadResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await authFetch(`/api/pharmacy-inventory-import/upload${dryRun ? '?dry_run=1' : ''}`, { method: 'POST', body: fd });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new Error(data?.error || `Upload failed (HTTP ${res.status})`);
  return data as InventoryUploadResult;
}

// ── Inventory & dispensing (routes/pharmacy.js) ──────────────────
/**
 * Stock at the active branch. A selling screen passes its `channel` and gets
 * only what it can actually sell (its channel's store); `storeId` reads one
 * store; neither reads every store (Reports, Dashboard, the branch view).
 */
export function fetchInventory(filter: InventoryFilter = {}): Promise<InventoryItem[]> {
  const query: Record<string, string | number> = {};
  if (filter.channel) query.channel = filter.channel;
  if (filter.storeId) query.store_id = filter.storeId;
  return api.get('/pharmacy/inventory', { query });
}

// ── Pharmacy stores ──────────────────────────────────────────────
export function fetchPharmacyStores(): Promise<PharmacyStoresSnapshot> {
  return api.get('/pharmacy/stores');
}

export function fetchPharmacyStoresAdmin(): Promise<PharmacyStoresAdminSnapshot> {
  return api.get('/admin/pharmacy-stores');
}

export interface PharmacyStoreInput {
  code?: string;
  name?: string;
  store_type?: PharmacyStoreType;
  can_receive?: boolean;
  can_dispense?: boolean;
  active?: boolean;
  auto_receive_transfers?: boolean;
}

export function createPharmacyStore(input: PharmacyStoreInput): Promise<PharmacyStore> {
  return api.post('/admin/pharmacy-stores', input);
}

export function updatePharmacyStore(id: number, input: PharmacyStoreInput): Promise<PharmacyStore> {
  return api.patch(`/admin/pharmacy-stores/${id}`, input);
}

export function setPharmacyChannels(map: Partial<Record<PharmacyChannel, number>>): Promise<PharmacyStoresAdminSnapshot> {
  return api.put('/admin/pharmacy-stores/channels', map);
}

/** The near-expiry window + the later-expiry-batch reason list — see batchSelection.ts. */
export function fetchDispensingSettings(): Promise<DispensingSettings> {
  return api.get('/pharmacy/dispensing-settings');
}

/** The Near-Expiry Stock report for the active branch. */
export function fetchNearExpiryReport(selectionsDays: number, storeId?: number): Promise<NearExpiryReport> {
  return api.get('/pharmacy/near-expiry', { query: { selections_days: selectionsDays, ...(storeId ? { store_id: storeId } : {}) } });
}

/** Admin Panel → Pharmacy → Dispensing & Expiry (admin:pharmacy-loyalty). */
export function fetchDispensingSettingsAdmin(): Promise<DispensingSettingsAdmin> {
  return api.get('/admin/pharmacy-dispensing/settings');
}

export function updateDispensingSettings(input: { near_expiry_days?: number; adjustment_approval_units?: number | null; adjustment_approval_value?: number | null; reorder_lead_days?: number; reorder_review_days?: number; reorder_safety_days?: number }): Promise<DispensingSettingsAdmin> {
  return api.patch('/admin/pharmacy-dispensing/settings', input);
}

/** One batch's full receipt/dispense/adjustment ledger, newest first. */
export function fetchInventoryHistory(inventoryId: number): Promise<InventoryMovement[]> {
  return api.get(`/pharmacy/inventory/${inventoryId}/history`);
}

/**
 * Structured, active prescriptions ready to dispense against — branch-scoped
 * (routes/prescriptions.js GET /, same query the Consultation module lists
 * a visit's prescription with). Lives here, not in features/consultation,
 * for the same reason createPharmacyBill does: organized by which screen
 * calls it, not by which backend router owns the route.
 */
export function fetchActivePrescriptions(): Promise<DispensablePrescription[]> {
  return api.get('/prescriptions', { query: { status: 'active' } });
}

/** Full item detail — including per-item dispensed/remaining quantity — for one prescription. */
export function fetchPrescriptionDetail(id: number): Promise<DispensablePrescriptionDetail> {
  return api.get(`/prescriptions/${id}`);
}

export function createStockAdjustment(input: CreateStockAdjustmentInput) {
  return api.post('/pharmacy/stock-adjustments', input);
}

/**
 * Lives here, not in features/billing, even though it hits /billing/*  —
 * organized by which screen actually calls it (Dispensing, exclusively),
 * matching how every other migrated feature this session is organized by
 * screen rather than by backend router.
 */
export function createPharmacyBill(input: CreatePharmacyBillInput): Promise<PharmacyBillResult> {
  return api.post('/billing/pharmacy/create', input);
}

/**
 * A cart line linked to a prescription item (prescription_item_id) whose
 * actual drug identity doesn't match what was prescribed on that line —
 * POST /pharmacy/create HARD-BLOCKS this unless the same line carries a
 * substitution_reason; this pre-checkout check exists so the pharmacist can
 * supply that reason before hitting the block, not after. See
 * utils/rxSafetyChecks.js's matchesPrescribedDrug.
 */
export interface DispenseMismatch {
  inventory_id: number;
  prescription_item_id: number;
  prescribed_drug: string;
  dispensed_drug: string;
}

/**
 * Runs the same deterministic allergy/duplicate-therapy check AND the same
 * prescribed-drug-identity check POST /pharmacy/create runs internally —
 * WITHOUT selling anything — so the pharmacist can be warned before
 * checkout. See utils/rxSafetyChecks.js's checkDispensingSafety and
 * matchesPrescribedDrug.
 */
export function checkDispensingSafety(
  patientId: number,
  items: { inventory_id: number; prescription_item_id?: number }[]
): Promise<{ alerts: SafetyAlertLike[]; mismatches: DispenseMismatch[] }> {
  return api.post('/billing/pharmacy/check', { patient_id: patientId, items });
}

// ── Drug-substitution governance (routes/pharmacy-substitutions.js) ──────

/**
 * The pre-checkout half of the approval workflow — called from
 * SubstitutePicker's onConfirm on both dispensing screens. A no-op
 * (`requires_approval: false`) unless the pair is a genuine drug mismatch
 * AND the tenant is in blocking mode, in which case it creates/reuses a
 * pending request the pharmacist must wait on before the line can be
 * checked out. See useSubstitutionApproval, which wraps this call plus the
 * polling that follows it.
 */
export function requestSubstitutionApproval(
  prescriptionItemId: number,
  dispensedInventoryId: number,
  reason: string
): Promise<SubstitutionRequestResult> {
  return api.post('/pharmacy/substitutions/requests', {
    prescription_item_id: prescriptionItemId,
    dispensed_inventory_id: dispensedInventoryId,
    reason,
  });
}

export function fetchSubstitutionRequest(id: number): Promise<SubstitutionReview> {
  return api.get(`/pharmacy/substitutions/requests/${id}`);
}

/** Doctor-only — the in-app pending-approvals badge's poll target. */
export function fetchPendingSubstitutionCount(): Promise<{ pending: number }> {
  return api.get('/pharmacy/substitutions/count');
}

/** Doctor-only — the badge's detail list. */
export function fetchPendingSubstitutions(): Promise<SubstitutionReview[]> {
  return api.get('/pharmacy/substitutions/pending');
}

export function approveSubstitution(id: number): Promise<SubstitutionReview> {
  return api.patch(`/pharmacy/substitutions/${id}/approve`);
}

export function rejectSubstitution(id: number, reason: string): Promise<SubstitutionReview> {
  return api.patch(`/pharmacy/substitutions/${id}/reject`, { reason });
}

// ── Procurement (routes/pharmacy-procurement.js) ─────────────────
export function fetchVendors(): Promise<Vendor[]> {
  return api.get('/pharmacy-procurement/vendors');
}

export function createVendor(input: NewVendorInput): Promise<Vendor> {
  return api.post('/pharmacy-procurement/vendors', input);
}

export function fetchPurchaseOrders(): Promise<PurchaseOrder[]> {
  return api.get('/pharmacy-procurement/purchase-orders');
}

/** Enriched single PO (items joined to medicines where resolved) — used only
 * to pre-fill the "Mark Received" form. */
export function fetchPurchaseOrder(id: number): Promise<PurchaseOrderDetail> {
  return api.get(`/pharmacy-procurement/purchase-orders/${id}`);
}

export function createPurchaseOrder(input: NewPurchaseOrderInput): Promise<PurchaseOrder> {
  return api.post('/pharmacy-procurement/purchase-orders', input);
}

export function updatePOStatus(id: number, status: POStatus) {
  return api.patch(`/pharmacy-procurement/purchase-orders/${id}/status`, { status });
}

export function fetchGoodsReceipts(): Promise<GoodsReceipt[]> {
  return api.get('/pharmacy-procurement/goods-receipts');
}

export function createGoodsReceipt(input: NewGoodsReceiptInput): Promise<GoodsReceipt> {
  return api.post('/pharmacy-procurement/goods-receipts', input);
}

export function fetchScheduleRegister(): Promise<ScheduleRegisterEntry[]> {
  return api.get('/pharmacy-procurement/schedule-register');
}

// ── Vendor invoice upload ─────────────────────────────────────────
// The actual multipart upload (uploadVendorInvoice) lives in src/api.js —
// see that file's own comment — and is imported directly at the call site,
// same convention PrescriptionPanel.tsx already follows for
// uploadPatientDocument. These are the plain-JSON endpoints around it.

/**
 * Mints a branch-scoped "scan to upload" QR session, optionally tied to a
 * PO. provider/model are the staff browser's own AI Settings choice
 * (src/aiConfig.ts) — captured here because the phone that actually
 * uploads is an anonymous unauthenticated page with no AI config of its
 * own; the backend stores them on the session and uses them when the
 * phone's upload triggers extraction.
 */
export function createVendorInvoiceMobileSession(poId?: number, provider?: string, model?: string): Promise<VendorInvoiceMobileSession> {
  return api.post('/pharmacy-procurement/vendor-invoices/mobile-session', { po_id: poId, origin: window.location.origin, provider, model });
}

/** Polled while the QR modal is open. */
export function getVendorInvoiceMobileSessionStatus(token: string): Promise<VendorInvoiceMobileSessionStatus> {
  return api.get(`/pharmacy-procurement/vendor-invoices/mobile-session/${token}/status`);
}

/** Resume review after a mobile upload lands, or a slow extraction finishes. */
export function fetchVendorInvoiceUpload(id: number): Promise<VendorInvoiceUpload> {
  return api.get(`/pharmacy-procurement/vendor-invoices/${id}`);
}

// ── Drug Master product resolution (routes/medicines.js) ─────────
// Shared by the Goods Receipt and Purchase Order pickers — search first
// (pick an existing product), or "Declare new brand" via createMedicine
// (routes/medicines.js's POST /, see
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §20-§21). Never a
// silent match-or-create: the caller always makes an explicit choice.
export function searchMedicines(q: string): Promise<MedicineSearchResult[]> {
  if (!q.trim()) return Promise.resolve([]);
  return api.get('/medicines', { query: { q } });
}

export function createMedicine(input: NewMedicineInput): Promise<MedicineSearchResult> {
  return api.post('/medicines', input);
}

// ── Walk-in counter (routes/pharmacy-outpatient.js) ──────────────
//
// Every call below is gated server-side on the `pharmacy:outpatient` grant,
// separately from the `pharmacy` module the tabs above use — the counter
// takes money and issues refunds, which is not the same permission as
// looking at stock. See migration_pharmacy_outpatient.sql.

/**
 * Finds an EXISTING patient by mobile, name or UHID before offering to
 * register a new one. Searching the one patient directory (not a separate
 * customer list) is what makes "she consulted here last year and now buys her
 * BP tablets monthly" one record rather than two — her counter purchases show
 * up in Patient Records -> Billing alongside her OPD bills, and her points
 * survive between the two.
 */
export function searchCustomers(q: string): Promise<CounterCustomer[]> {
  return api.get('/pharmacy-outpatient/customers/search', { query: { q } });
}

/** Registers a genuinely new walk-in as a full patient record, with a real UHID. */
export function registerCustomer(input: { name: string; mobile: string; gender?: string; age?: number; address?: string; email?: string }): Promise<CounterCustomer> {
  return api.post('/pharmacy-outpatient/customers', input);
}

export function fetchCustomer(patientId: number): Promise<CounterCustomerDetail> {
  return api.get(`/pharmacy-outpatient/customers/${patientId}`);
}

/** What this patient has bought before, resolved to today's FEFO batch. */
export function fetchRepeatMedicines(patientId: number): Promise<RepeatMedicine[]> {
  return api.get(`/pharmacy-outpatient/customers/${patientId}/recent-medicines`);
}

export function fetchCustomerPurchases(patientId: number): Promise<OutpatientSale[]> {
  return api.get(`/pharmacy-outpatient/customers/${patientId}/purchases`);
}

export function captureOutpatientPrescription(input: OutpatientPrescriptionInput): Promise<OutpatientPrescription> {
  return api.post('/pharmacy-outpatient/prescriptions', input);
}

/**
 * The sale. Runs through the same backend engine (utils/pharmacySale.js) as
 * the visit-linked Dispensing tab — same stock locking, same GST, same
 * invoice series, same bill tables — with a discount, an outside
 * prescription and loyalty layered on top.
 */
export function createOutpatientSale(input: CreateOutpatientSaleInput): Promise<OutpatientSaleResult> {
  return api.post('/pharmacy-outpatient/sales', input);
}

export function fetchOutpatientSales(params?: { date?: string; channel?: 'all' | 'outpatient' | 'admission' }): Promise<OutpatientSale[]> {
  return api.get('/pharmacy-outpatient/sales', { query: { date: params?.date, channel: params?.channel } });
}

export function fetchOutpatientSale(id: number): Promise<OutpatientSaleDetail> {
  return api.get(`/pharmacy-outpatient/sales/${id}`);
}

/**
 * Takes medicine back. The bill is never deleted — GST law requires the
 * invoice series to stay unbroken — so this creates its own PHRET/... document
 * and marks the original partly returned or cancelled.
 */
export function createPharmacyReturn(saleId: number, input: CreateReturnInput): Promise<PharmacyReturn> {
  return api.post(`/pharmacy-outpatient/sales/${saleId}/returns`, input);
}

/**
 * `status: 'Pending'` deliberately ignores `date` server-side — an unsettled
 * refund raised three days ago still has to be paid out today, so scoping the
 * outstanding list to one day would hide the rows it exists to surface.
 */
export function fetchPharmacyReturns(params?: { date?: string; status?: 'Paid' | 'Pending' }): Promise<PharmacyReturn[]> {
  return api.get('/pharmacy-outpatient/returns', { query: { date: params?.date, status: params?.status } });
}

/** Records that a Pending refund has now actually been handed over. */
export function settlePharmacyRefund(returnId: number, refundMode?: PaymentMode): Promise<PharmacyReturn> {
  return api.patch(`/pharmacy-outpatient/returns/${returnId}/settle`, { refund_mode: refundMode });
}

// ── Loyalty (routes/loyalty.js) ──────────────────────────────────

export function fetchLoyaltySettings(): Promise<LoyaltySettings> {
  return api.get('/loyalty/settings');
}

export function fetchLoyaltyBalance(patientId: number): Promise<LoyaltyBalance> {
  return api.get(`/loyalty/balance/${patientId}`);
}

export function fetchLoyaltyHistory(patientId: number): Promise<LoyaltyLedgerEntry[]> {
  return api.get(`/loyalty/history/${patientId}`);
}

/**
 * Advisory. The sale endpoint re-quotes server-side before spending anything,
 * since this answer can be seconds stale by the time the operator clicks save.
 */
export function quoteLoyalty(patientId: number, amount: number): Promise<LoyaltyQuote> {
  return api.get('/loyalty/quote', { query: { patient_id: patientId, amount } });
}

// ── Medicine master (G10) — GET/PATCH /api/medicines/:id, contract §6v ──
export interface ProductPurchase {
  id: number;
  grn_number: string;
  received_at: string;
  vendor_invoice_number: string | null;
  vendor_name: string | null;
  batch_number: string;
  expiry_date: string;
  mfg_date: string | null;
  quantity_received: number;
  invoiced_quantity: number | null;
  discrepancy_reason: string | null;
  units_per_pack: number | null;
  unit_cost: string | null;
  mrp: string;
  unit_landed_cost: string | null;
}

export interface ProductDetail {
  id: number;
  generic_name: string;
  brand_name: string | null;
  strength: string | null;
  dosage_form: string | null;
  hsn_code: string | null;
  gst_percent: string | null;
  schedule: string;
  category: string | null;
  pack_size: number | null;
  preferred_vendor_id: number | null;
  preferred_vendor_name: string | null;
  manufacturer_name: string | null;
  drug_name: string | null;
  active: boolean;
  stock_by_store: { store_id: number; store_name: string; units: number; batches: number; earliest_expiry: string | null; min_level: number | null; max_level: number | null }[];
  purchases: ProductPurchase[];
  last_purchase: { received_at: string; vendor_name: string | null; unit_landed_cost: string | null; mrp: string } | null;
}

export type ProductPatch = Partial<{
  brand_name: string | null; strength: string | null; dosage_form: string | null; hsn_code: string | null;
  gst_percent: number; schedule: string; category: string | null; pack_size: number | null;
  preferred_vendor_id: number | null; active: boolean;
}>;

export function fetchProduct(id: number): Promise<ProductDetail> {
  return api.get<ProductDetail>(`/medicines/${id}`);
}

export function updateProduct(id: number, patch: ProductPatch): Promise<ProductDetail> {
  return api.patch<ProductDetail>(`/medicines/${id}`, patch);
}

// ── Declined quantities (G9) — contract §6v ──
export function declinePrescriptionItem(itemId: number, input: { quantity: number; reason: string; note?: string }) {
  return api.post<{ decline: unknown; prescription: unknown }>(`/prescriptions/items/${itemId}/decline`, input);
}

export function undoPrescriptionDecline(declineId: number, reason?: string) {
  return api.post<{ decline: unknown; prescription: unknown }>(`/prescriptions/declines/${declineId}/undo`, reason ? { reason } : {});
}

